import type { Action, ThunkAction } from '@reduxjs/toolkit';

import { generateMessageId } from '../utils';
import { setPendingHostUserMessage } from './aiSidekickChatSlice';
import type { StateWithSidekickChat } from './chatTypes';

/**
 * Queues a host-originated user message for `ChatInterface` to submit
 * through `useConversation.sendMessage`. Hosts that auto-submit from
 * outside the panel (e.g., MF Insights entry-card chips) should use this
 * instead of constructing a `Message` themselves. Trims `content` and
 * is a no-op when the trimmed string is empty.
 *
 * Also bails when the last message in the slice is already a user
 * turn awaiting an assistant reply, so rapid host-side double-dispatch
 * (e.g. double-clicking an entry-card chip while the panel is opening)
 * cannot queue duplicate requests back-to-back. This mirrors the
 * in-flight debounce inside `useConversation.sendMessage` for the
 * InputBar path so both submission surfaces share one-user-turn-at-a-time
 * semantics.
 *
 * Post-SDPCDXP-1619: errors no longer append an assistant message —
 * they append an `ErrorEvent` keyed to `sourceUserMessageId`. So a
 * failed user turn leaves `role:'user'` as the last `messages[]`
 * entry forever. The guard treats a user message with an associated
 * `ErrorEvent` as terminated (the host can submit a follow-up) unless
 * a retry is currently in flight (`retryingErrorId !== null`), in
 * which case the user turn is still busy waiting on the retry result.
 *
 * Returns whether the message was queued.
 */
export const dispatchHostUserMessage =
  (
    content: string
  ): ThunkAction<boolean, StateWithSidekickChat, unknown, Action> =>
  (dispatch, getState) => {
    const trimmed = content.trim();
    if (trimmed.length === 0) {
      return false;
    }
    const {
      messages,
      errorEvents,
      isStreaming,
      pendingHostUserMessage,
      retryingErrorId,
    } = getState().aiSidekickChat;
    if (isStreaming || pendingHostUserMessage !== null) {
      return false;
    }
    const lastMessage = messages[messages.length - 1];
    if (lastMessage !== undefined && lastMessage.role === 'user') {
      const lastUserTerminatedByError = errorEvents.some(
        (e) => e.sourceUserMessageId === lastMessage.id
      );
      if (!lastUserTerminatedByError || retryingErrorId !== null) {
        return false;
      }
    }
    dispatch(
      setPendingHostUserMessage({
        id: generateMessageId(),
        content: trimmed,
      })
    );
    return true;
  };
